import React, { useState, useEffect } from 'react';
import axios from '../axiosConfig';
import { FaTimes } from 'react-icons/fa';

const AddProjectModal = ({ onClose, onProjectAdded }) => {
  const [title, setTitle] = useState('');
  const [clientIds, setClientIds] = useState([]);
  const [status, setStatus] = useState('Pending');
  const [parentProjectId, setParentProjectId] = useState('');
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const loggedIn = !!token;
    setIsLoggedIn(loggedIn);

    if (!loggedIn) return;

    const fetchData = async () => {
      try {
        const [clientsRes, projectsRes] = await Promise.all([
          axios.get('/clients/'),
          axios.get('/projects/'),
        ]);

        setClients(
          Array.isArray(clientsRes.data.results)
            ? clientsRes.data.results
            : clientsRes.data
        );

        setProjects(
          Array.isArray(projectsRes.data.results)
            ? projectsRes.data.results
            : projectsRes.data
        );
      } catch (err) {
        console.error('❌ Failed to fetch clients/projects:', err);
      }
    };

    fetchData();
  }, []);

  if (!isLoggedIn) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || clientIds.length === 0) return;

    try {
      setLoading(true);

      const payload = {
        title: title.trim(),
        status,
        clients: clientIds.map((id) => parseInt(id)),
        ...(parentProjectId && { parent_project: parseInt(parentProjectId) }),
      };

      await axios.post('/projects/', payload);

      onClose();
      onProjectAdded?.();
    } catch (err) {
      console.error('❌ Project creation failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="relative bg-white rounded-2xl shadow-xl p-6 w-full max-w-xl animate-fadeIn">
        {/* ❌ Close icon */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-red-600 text-lg"
        >
          <FaTimes />
        </button>

        <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
          ➕ Add New Project
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Title
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 px-4 py-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Enter project title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Clients */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client(s)
            </label>
            <select
              multiple
              className="w-full border border-gray-300 px-4 py-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={clientIds}
              onChange={(e) =>
                setClientIds(Array.from(e.target.selectedOptions, (opt) => opt.value))
              }
              required
            >
              {clients.length > 0 ? (
                clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name || client.email || `Client ${client.id}`}
                  </option>
                ))
              ) : (
                <option disabled>No clients found</option>
              )}
            </select>
            <small className="text-xs text-gray-500">
              Hold Ctrl (Windows) or Cmd (Mac) to select multiple
            </small>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="w-full border border-gray-300 px-4 py-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Complete">Complete</option>
            </select>
          </div>

          {/* Parent Project */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parent Project (optional)
            </label>
            <select
              className="w-full border border-gray-300 px-4 py-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              value={parentProjectId}
              onChange={(e) => setParentProjectId(e.target.value)}
            >
              <option value="">-- None --</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 rounded-xl text-white font-semibold transition ${
                loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProjectModal;